import { useState, useRef, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { Sparkles, Loader2, FileText, Send, AlertTriangle } from 'lucide-react';
import { cn } from '../../lib/utils';
import { toast } from 'sonner';
import api from '../../lib/api';

const PROGRESS_MESSAGES = [
  'Analyzing segments…',
  'Building insights…',
  'Cross-referencing demographics…',
  'Drafting recommendations…',
];

const SUGGESTED_PROMPTS = [
  'What are the key findings from this simulation?',
  'Which demographic groups show the most resistance?',
  'What communication strategy would you recommend?',
  'Summarise the top 3 risks in 5 lines.',
];

const REQUEST_TIMEOUT_MS = 15000; // 15 seconds

function withTimeout(promise, ms) {
  let timer;
  const timeoutPromise = new Promise((_, reject) => {
    timer = setTimeout(() => reject(new Error('AI_INSIGHTS_TIMEOUT')), ms);
  });
  return Promise.race([promise, timeoutPromise]).finally(() => clearTimeout(timer));
}

export function AIInsightsTab({ selectedSimId }) {
  const [chatHistory, setChatHistory] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [insightLoading, setInsightLoading] = useState(false);
  const [insights, setInsights] = useState(null);
  const [progressIdx, setProgressIdx] = useState(0);
  const chatEndRef = useRef(null);

  // Reset chat when sim changes
  useEffect(() => {
    setChatHistory([]);
    setInsights(null);
  }, [selectedSimId]);

  // Rotate progress message every ~2.5s while loading
  useEffect(() => {
    if (!insightLoading) {
      setProgressIdx(0);
      return undefined;
    }
    const id = setInterval(() => {
      setProgressIdx((i) => (i + 1) % PROGRESS_MESSAGES.length);
    }, 2500);
    return () => clearInterval(id);
  }, [insightLoading]);

  const handleAiTimeout = () => {
    toast.warning('AI Insights is taking too long', {
      description: 'Showing a quick summary instead. Click retry to try again.',
    });
    setChatHistory((prev) => [
      ...prev,
      {
        role: 'assistant',
        timedOut: true,
        content:
          'Heads up — the AI is taking longer than usual to respond. Try a more specific question (e.g. "Compare engineering vs sales") or hit retry. Your simulation data is loaded; this is only the natural-language summary that\'s slow.',
      },
    ]);
  };

  const sendChatMessage = async (overridePrompt) => {
    const userMessage = (overridePrompt ?? chatInput).trim();
    if (!userMessage || !selectedSimId) return;
    setChatInput('');
    setChatHistory(prev => [...prev, { role: 'user', content: userMessage }]);
    setInsightLoading(true);
    try {
      const response = await withTimeout(
        api.post(`/simulations/${selectedSimId}/insights`, { question: userMessage }),
        REQUEST_TIMEOUT_MS,
      );
      setChatHistory(prev => [...prev, { role: 'assistant', content: response.data.insights }]);
      setTimeout(() => { chatEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, 100);
    } catch (error) {
      if (error.message === 'AI_INSIGHTS_TIMEOUT') {
        handleAiTimeout();
      } else {
        toast.error(error.response?.data?.detail || 'Failed to get response');
        setChatHistory(prev => prev.slice(0, -1));
      }
    } finally {
      setInsightLoading(false);
    }
  };

  const generateInsights = async () => {
    if (!selectedSimId) return;
    setInsightLoading(true);
    try {
      const response = await withTimeout(
        api.post(`/simulations/${selectedSimId}/insights`, { question: null }),
        REQUEST_TIMEOUT_MS,
      );
      setInsights(response.data);
    } catch (error) {
      if (error.message === 'AI_INSIGHTS_TIMEOUT') {
        toast.warning('Full report timed out', { description: 'Try again or ask a focused question instead.' });
      } else {
        toast.error('Failed to generate insights');
      }
    } finally {
      setInsightLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <Card className="border border-border">
        <CardHeader className="pb-2">
          <CardTitle className="text-lg flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-warning" />
            AI-Powered Insights Chat
          </CardTitle>
          <p className="text-sm text-muted-foreground mt-1">Ask questions about the simulation results and get AI-powered insights</p>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Chat History */}
          <div className="h-96 overflow-y-auto border border-border rounded-lg p-4 space-y-4 bg-muted/50">
            {chatHistory.length === 0 ? (
              <div className="h-full flex flex-col items-center justify-center text-center text-muted-foreground">
                <Sparkles className="w-12 h-12 mb-3 opacity-20" />
                <p className="text-sm font-medium">No conversations yet</p>
                <p className="text-xs mt-1">Ask a question to start analyzing your results</p>
                <div className="mt-4 space-y-2 text-xs text-left w-full max-w-md">
                  <p className="text-muted-foreground font-medium">Try asking:</p>
                  {SUGGESTED_PROMPTS.map(q => (
                    <button
                      key={q}
                      onClick={() => sendChatMessage(q)}
                      className="block w-full text-left px-3 py-2 rounded-md bg-background hover:bg-muted transition-colors border border-border"
                      data-testid={`suggested-prompt-${q.slice(0, 18).replace(/\s+/g, '-')}`}
                    >
                      &quot;{q}&quot;
                    </button>
                  ))}
                </div>
              </div>
            ) : (
              <>
                {chatHistory.map((msg, idx) => (
                  <div key={idx} className={cn("flex gap-3", msg.role === 'user' ? "justify-end" : "justify-start")}>
                    <div className={cn(
                      "max-w-[80%] rounded-lg p-3 text-sm",
                      msg.role === 'user'
                        ? "bg-primary text-primary-foreground"
                        : msg.timedOut
                          ? "bg-warning-soft  border border-warning-border  text-warning-soft-foreground "
                          : "bg-background border border-border text-foreground",
                    )}>
                      {msg.timedOut && (
                        <div className="flex items-center gap-1.5 mb-1.5 text-xs font-semibold">
                          <AlertTriangle className="w-3.5 h-3.5" />
                          AI took too long
                        </div>
                      )}
                      <div className="whitespace-pre-wrap">{msg.content}</div>
                    </div>
                  </div>
                ))}
                {insightLoading && (
                  <div className="flex gap-3 justify-start" data-testid="ai-insights-loading">
                    <div className="bg-background border border-border rounded-lg p-3 text-sm text-muted-foreground flex items-center gap-2 min-w-[220px]">
                      <Loader2 className="w-4 h-4 animate-spin" />
                      <span className="transition-opacity">{PROGRESS_MESSAGES[progressIdx]}</span>
                    </div>
                  </div>
                )}
                <div ref={chatEndRef} />
              </>
            )}
          </div>

          {/* Chat Input */}
          <div className="flex gap-2">
            <Textarea
              value={chatInput}
              onChange={(e) => setChatInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendChatMessage(); } }}
              placeholder="Ask a question about the simulation results..."
              rows={2}
              className="resize-none"
              data-testid="chat-input"
            />
            <Button onClick={() => sendChatMessage()} disabled={!chatInput.trim() || insightLoading} className="bg-primary hover:bg-primary/90 shrink-0" data-testid="send-chat-btn">
              {insightLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            </Button>
          </div>

          {/* Always-visible suggested prompts (not just empty state) */}
          {chatHistory.length > 0 && (
            <div className="flex flex-wrap gap-1.5 pt-1" data-testid="suggested-prompts-row">
              <span className="text-[10px] uppercase tracking-wide text-slate-400 self-center mr-1">Quick prompts</span>
              {SUGGESTED_PROMPTS.slice(0, 3).map(q => (
                <button
                  key={q}
                  onClick={() => sendChatMessage(q)}
                  disabled={insightLoading}
                  className="text-[11px] px-2 py-1 rounded border border-slate-200 dark:border-border bg-slate-50 dark:bg-muted text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-muted/80 disabled:opacity-50 transition-colors"
                >
                  {q.length > 40 ? q.slice(0, 38) + '…' : q}
                </button>
              ))}
            </div>
          )}

          {/* Quick Generate */}
          <div className="pt-2 border-t border-border">
            <p className="text-xs text-muted-foreground mb-2">Or generate a full report:</p>
            <Button onClick={generateInsights} disabled={insightLoading} variant="outline" size="sm" className="w-full" data-testid="generate-full-report-btn">
              {insightLoading ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" />{PROGRESS_MESSAGES[progressIdx]}</>) : (<><FileText className="w-4 h-4 mr-2" />Generate Full Analysis Report</>)}
            </Button>
          </div>

          {insights && (
            <Card className="mt-4 bg-warning-soft border-warning-border">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm flex items-center gap-2">
                  <Sparkles className="w-4 h-4 text-warning-soft-foreground" />Full Analysis Report
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="prose prose-sm prose-slate dark:prose-invert max-w-none">
                  <div className="whitespace-pre-wrap text-foreground text-sm">{insights.insights}</div>
                </div>
                <p className="text-xs text-muted-foreground mt-4">Generated {new Date(insights.generated_at).toLocaleString()}</p>
              </CardContent>
            </Card>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
